import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, Text, Group, Box, Loader, Alert, Grid, Container, Title, Image, Button } from '@mantine/core';
import { useSadhanaData } from '../hooks/useSadhanaData';
import { getSundogLogoUrl } from '../firebase/storageService';

const Header = () => {
    // Get current location for active nav highlighting
    const location = useLocation();
    
    // State for the logo URL
    const [logoUrl, setLogoUrl] = useState(null);
    const [logoLoading, setLogoLoading] = useState(true);

    // Use the Firebase hook for data management
    const {
        sadhanaData,
        loading,
        error,
        formattedDate,
        locationLabel
    } = useSadhanaData();

    // Load the sundog logo from Firebase Storage
    useEffect(() => {
        const loadLogo = async () => {
            try {
                setLogoLoading(true);
                const url = await getSundogLogoUrl();
                setLogoUrl(url);
            } catch (error) {
                console.error('Error loading logo:', error);
            } finally {
                setLogoLoading(false);
            }
        };

        loadLogo();
    }, []);

    const handleLocationClick = () => {
        if (sadhanaData.location && sadhanaData.location !== 'online') {
            const locationMap = {
                'high-park': 'High+Park+Toronto',
                'trinity-bellwoods': 'Trinity+Bellwoods+Park+Toronto',
                'centre-island': 'Centre+Island+Toronto',
                'harbourfront': 'Harbourfront+Centre+Toronto'
            };
            const query = locationMap[sadhanaData.location];
            if (query) {
                window.open(`https://www.google.com/maps/search/${query}`, '_blank');
            }
        }
    };

    return (
        <header>
            <style>
                {`
                    .header-container {
                        min-height: 120px;
                        padding: 1rem 0;
                    }
                    
                    .responsive-slim-card {
                        max-width: 640px !important;
                        width: 100% !important;
                        height: 60px !important;
                        display: flex !important;
                        align-items: center !important;
                    }
                    
                    /* Wide screens: Make card wider */
                    @media (min-width: 900px) {
                        .responsive-slim-card {
                            max-width: 768px !important;
                            width: 768px !important;
                        }
                    }
                    
                    /* Narrow screens: Full width card and smaller text */
                    @media (max-width: 768px) {
                        .responsive-slim-card {
                            max-width: 100vw !important;
                            margin: 0 -20px !important;
                            border-radius: 0 !important;
                        }
                        .header-title-mobile h1 {
                            font-size: 1.4rem !important;
                        }
                        .header-subtitle-mobile {
                            font-size: 0.8rem !important;
                        }
                    }
                `}
            </style>
            <Container size="xl" className="header-container">
                <Grid align="center" justify="space-between">
                    {/* Title Section */}
                    <Grid.Col span={{ base: 12, sm: 12, md: 6, lg: 6 }}>
                        <Box
                            style={{
                                textAlign: { base: 'center', md: 'left' },
                                paddingLeft: { base: 0, lg: '2rem' }
                            }}
                        >
                            <Group
                                gap={{ base: 'xs', md: 'sm' }}
                                justify={{ base: 'center', md: 'flex-start' }}
                                align="center"
                                wrap="nowrap"
                                className="header-title-mobile"
                            >
                                {/* Logo */}
                                {logoUrl && (
                                    <Image
                                        src={logoUrl}
                                        alt="Amrit Canada Logo"
                                        style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            boxShadow: '0 2px 8px rgba(244, 196, 48, 0.3)'
                                        }}
                                    />
                                )}
                                {logoLoading && (
                                    <Box style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Loader size="xs" color="var(--saffron-bright)" />
                                    </Box>
                                )}
                                
                                <Box>
                                    <Title
                                        order={1}
                                        size={{ base: '1.4rem', sm: '1.6rem', md: '1.8rem' }}
                                        style={{
                                            margin: 0,
                                            color: 'var(--saffron-bright)',
                                            fontWeight: 600,
                                            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.4)',
                                            letterSpacing: '-0.5px'
                                        }}
                                    >
                                        Amrit Canada
                                    </Title>
                                    <Text
                                        size={{ base: '0.8rem', sm: '0.85rem', md: '0.9rem' }}
                                        className="header-subtitle-mobile"
                                        style={{
                                            margin: 0,
                                            color: 'var(--saffron-medium)',
                                            fontStyle: 'italic',
                                            fontWeight: 300,
                                            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        Crown your self in the early hours of the morning
                                    </Text>
                                </Box>
                            </Group>
                            
                            {/* Small Navigation at bottom left */}
                            <Group gap="xs" justify={{ base: 'center', md: 'flex-start' }} mt="sm" style={{ paddingLeft: { base: 0, lg: '2rem' } }}>
                                <Button
                                    component={Link}
                                    to="/"
                                    variant={location.pathname === '/' ? 'filled' : 'outline'}
                                    color="var(--saffron-bright)"
                                    size="xs"
                                    style={{
                                        backgroundColor: location.pathname === '/' ? 'var(--saffron-bright)' : 'transparent',
                                        borderColor: 'var(--saffron-bright)',
                                        color: location.pathname === '/' ? 'white' : 'var(--saffron-bright)',
                                        fontWeight: 500,
                                        fontSize: '0.7rem',
                                        height: '24px',
                                        padding: '0 8px'
                                    }}
                                >
                                    Home
                                </Button>
                                <Button
                                    component={Link}
                                    to="/sadhana"
                                    variant={location.pathname === '/sadhana' ? 'filled' : 'outline'}
                                    color="var(--saffron-bright)"
                                    size="xs"
                                    style={{
                                        backgroundColor: location.pathname === '/sadhana' ? 'var(--saffron-bright)' : 'transparent',
                                        borderColor: 'var(--saffron-bright)',
                                        color: location.pathname === '/sadhana' ? 'white' : 'var(--saffron-bright)',
                                        fontWeight: 500,
                                        fontSize: '0.7rem',
                                        height: '24px',
                                        padding: '0 8px'
                                    }}
                                >
                                    Sadhana
                                </Button>
                            </Group>
                        </Box>
                    </Grid.Col>

                    {/* Card Section - Restored to full size */}
                    <Grid.Col span={{ base: 12, sm: 12, md: 6, lg: 6 }}>
                        <Box style={{
                            display: 'flex',
                            justifyContent: { base: 'center', md: 'flex-end' },
                            paddingRight: { base: 0, lg: '2rem' }
                        }}>
                            {(formattedDate || locationLabel) && (
                                <>
                                    {/* Loading State */}
                                    {loading && (
                                        <Box ta="center" mb="md">
                                            <Loader color="var(--saffron-bright)" size="sm" />
                                            <Text size="sm" c="dimmed" mt="xs">Loading Sadhana information...</Text>
                                        </Box>
                                    )}

                                    {/* Error State */}
                                    {error && (
                                        <Alert color="orange" size="sm" mb="md">
                                            <Text size="xs">
                                                Unable to load from Firebase. Showing offline data.
                                            </Text>
                                        </Alert>
                                    )}

                                    {/* Responsive Slim Card */}
                                    <Card
                                        shadow="sm"
                                        padding="xs"
                                        radius="md"
                                        withBorder
                                        className="responsive-slim-card"
                                        style={{
                                            background: 'linear-gradient(145deg, var(--cream) 0%, var(--linen) 100%)',
                                            border: '2px solid var(--saffron-medium)',
                                            boxShadow: '0 4px 16px rgba(244, 196, 48, 0.2)'
                                        }}
                                    >
                                        <Group justify="space-between" align="center" style={{ width: '100%', height: '100%' }}>
                                            {/* Date Field */}
                                            {formattedDate && (
                                                <Box ta="center" style={{ flex: 1 }}>
                                                    <Text size="xs" c="dimmed" fw={500} style={{ lineHeight: 1.2 }}>DATE</Text>
                                                    <Text size="sm" fw={600} style={{ color: 'var(--charcoal)', lineHeight: 1.2 }}>
                                                        {formattedDate}
                                                    </Text>
                                                </Box>
                                            )}

                                            {/* Divider */}
                                            {formattedDate && locationLabel && (
                                                <Box style={{
                                                    width: '1px',
                                                    height: '30px',
                                                    backgroundColor: '#dee2e6'
                                                }} />
                                            )}

                                            {/* Location Field */}
                                            {locationLabel && (
                                                <Box ta="center" style={{ flex: 1 }}>
                                                    <Text size="xs" c="dimmed" fw={500} style={{ lineHeight: 1.2 }}>LOCATION</Text>
                                                    <Text
                                                        size="sm"
                                                        fw={600}
                                                        style={{ color: 'var(--saffron-bright)', cursor: 'pointer', lineHeight: 1.2 }}
                                                        onClick={handleLocationClick}
                                                    >
                                                        {locationLabel}
                                                    </Text>
                                                </Box>
                                            )}
                                        </Group>
                                    </Card>
                                </>
                            )}
                        </Box>
                    </Grid.Col>
                </Grid>
            </Container>
        </header>
    );
};

export default Header;